import { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { Building2, AlertCircle, FileText, FileSignature } from "lucide-react";
import { fetchAllMasterData } from "@/store/slices/masterSlice";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCfcSubsidies,
  resetCfcState,
  setCurrentTab,
} from "../../store/slices/cfcSlice";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import SpvDetails from "./CfcForm/SpvDetails";
import PriorityDetails from "./CfcForm/PriorityDetails";
import DeclarationDetails from "./CfcForm/DeclarationDetails";
import { useLocation, useParams } from "react-router-dom";
import EligibilityDetails from "./CfcForm/EligibilityDetails";
import { Alert, AlertDescription } from "@/components/ui/alert";
import UploadRequirementsDialog from "../../components/common/UploadRequirementsDialog";
import { Button } from "../../components/ui/button";
import BankDetails from "./CfcForm/BankDetails";
import SanctionDetails from "./CfcForm/SanctionDetails";
import HowToApplyCFC from "../../assets/pdf/how_to_apply_cfc.pdf";

const CommonFacilityCentreForm = () => {
  const { current_tab, data, data_loading, error, user_initiated_nav } =
    useSelector((state) => state.cfc);

  const {
    districts = [],
    communities = [],
    loading,
  } = useSelector((state) => state.master || {});
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();
  const [completedTabs, setCompletedTabs] = useState(new Set());
  const [declarationCompleted, setDeclarationCompleted] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const tabsOrder = [
    "spv",
    "eligibility",
    "bank",
    "priority",
    "declaration",
    ...(data?.sanction?.status == "provisional_sanction_recommended"
      ? ["sanction_details"]
      : []),
  ];
  useEffect(() => {
    setOpenModal(true);
  }, []);

  useEffect(() => {
    dispatch(resetCfcState());
    dispatch(fetchAllMasterData());
    if (id) {
      dispatch(fetchCfcSubsidies(id));
    }
  }, [dispatch]);

  useEffect(() => {
    if (data) {
      const searchParams = new URLSearchParams(location.search);
      const tabParam = searchParams.get("tab");
      const newCompletedTabs = new Set();
      let nextTab = "spv";

      if (data?.id) {
        newCompletedTabs.add("spv");
        nextTab = "eligibility";
      }

      if (data?.id && data?.beneficiary?.is_spv) {
        newCompletedTabs.add("eligibility");
        nextTab = "bank";
      }

      if (
        data?.id &&
        data?.beneficiary?.is_spv &&
        data?.bank?.account_holder_name
      ) {
        newCompletedTabs.add("bank");
        nextTab = "priority";
      }

      if (
        data?.id &&
        data?.beneficiary?.is_spv &&
        data?.bank?.account_holder_name &&
        data?.additional_detail?.is_tie_up_made
      ) {
        newCompletedTabs.add("priority");
        nextTab = "declaration";
      }

      if (
        data?.id &&
        data?.beneficiary?.is_spv &&
        data?.bank?.account_holder_name &&
        data?.additional_detail?.is_tie_up_made &&
        data?.declaration?.terms_accepted
      ) {
        newCompletedTabs.add("declaration");
        if (data?.sanction?.status == "provisional_sanction_recommended") {
          nextTab = "sanction_details";
        }
      }

      if (
        data?.id &&
        data?.beneficiary?.is_spv &&
        data?.bank?.account_holder_name &&
        data?.additional_detail?.is_tie_up_made &&
        data?.declaration?.terms_accepted &&
        data?.sanction?.status == "provisional_sanction_recommended" &&
        data?.sanction?.is_user_accept
      ) {
        newCompletedTabs.add("sanction_details");
        nextTab = "sanction_details";
      }

      setCompletedTabs(newCompletedTabs);

      if (tabParam && tabsOrder.includes(tabParam)) {
        dispatch(setCurrentTab(tabParam));
      } else if (!user_initiated_nav) {
        dispatch(setCurrentTab(nextTab));
      }
    }
  }, [data, dispatch, location.search, user_initiated_nav]);

  const getCompletionPercentage = () => {
    const completedCount = completedTabs.size + (declarationCompleted ? 1 : 0);
    const totalTabs = tabsOrder.length;
    return Math.round((completedCount / totalTabs) * 100);
  };

  const isTabDisabled = (tabId) => {
    const tabIndex = tabsOrder.indexOf(tabId);
    if (tabIndex === 0) return false;

    for (let i = 0; i < tabIndex; i++) {
      if (!completedTabs.has(tabsOrder[i])) {
        return true;
      }
    }

    return false;
  };

  const handleTabComplete = (tabId) => {
    setCompletedTabs((prev) => new Set([...prev, tabId]));
  };

  const showCheckmark = (tabId) => {
    return completedTabs.has(tabId);
  };

  const uploads_array = [
    { name: "Pan Uploads", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    { name: "TAN Uploads", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    { name: "GST Uploads", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    { name: "Registration Uploads", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    {
      name: "Members Aadhaar Uploads",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    { name: "Bye Laws Uploads", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    { name: "Commitment Letter", type: "PDF, PNG, JPG, JPEG max size 2MB" },
    {
      name: "Capacity Utilization Evidence",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "Export Oriented Uploads",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    {
      name: "GPS Location of machine",
      type: "PNG, JPG, JPEG max size 2MB",
      notes:
        "Install the application in your android device -> https://play.google.com/store/apps/details?id=com.gpsmapcamera.geotagginglocationonphoto and capture the photo to upload",
    },
    {
      name: "Undertaking certificate for not selling",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
      notes:
        "Download Undertaking Certificate and print in 20 rupees stamp paper",
      file: "undertaking",
    },
    {
      name: "Basic Cluster Data Uploads",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
    { name: "Promoters Signature", type: "JPG, JPEG, PNG max size 2MB" },
    {
      name: "Declaration Document",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
      notes:
        "Download the declaration document, take a printout, and sign it with the date and place and Upload.",
      file: "declaration",
    },
    {
      name: "Other Supporting Documents",
      type: "PDF, PNG, JPG, JPEG max size 2MB",
    },
  ];

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  return (
    <div className="max-w-6xl mx-auto p-0 lg:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap justify-between gap-10 mb-10">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-primary">
            Common Facility Centre Application
          </h1>
          {data?.id && (
            <p className="text-muted-foreground">
              Application Number : {data?.application_number || "N/A"}
            </p>
          )}
        </div>
        <div className="space-y-2 flex flex-col justify-end gap-3">
          <Button
            type="button"
            size="sm"
            variant="default"
            className="text-xs"
            onClick={() => handleDownload(HowToApplyCFC, `how_to_apply.pdf`)}
          >
            How To Apply ?
          </Button>
          <Button
            type="button"
            size="sm"
            variant="default"
            className="text-xs"
            onClick={() => setOpenModal(true)}
          >
            Application Requirements
          </Button>
        </div>
      </div>
      {loading || data_loading ? (
        <div className="flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Application Progress</span>
              <span>{getCompletionPercentage()}% Complete</span>
            </div>
            <Progress value={getCompletionPercentage()} className="h-2" />
          </div>

          <Tabs
            value={current_tab}
            onValueChange={(value) => dispatch(setCurrentTab(value))}
            className="space-y-6"
          >
            <TabsList className={`w-full flex flex-wrap justify-evenly`}>
              <TabsTrigger
                value="spv"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("spv") ||
                  (data?.status &&
                    data?.status != "started" &&
                    data?.status != "incomplete")
                }
              >
                <Building2 className="h-4 w-4" />
                SPV Information
                {showCheckmark("spv") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="eligibility"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("eligibility") ||
                  (data?.status &&
                    data?.status != "started" &&
                    data?.status != "incomplete")
                }
              >
                <FileText className="h-4 w-4" />
                Beneficiary Details
                {showCheckmark("eligibility") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="bank"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("bank") ||
                  (data?.status && data?.status != "incomplete")
                }
              >
                <FileText className="h-4 w-4" />
                Bank Details
                {showCheckmark("bank") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>

              <TabsTrigger
                value="priority"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("priority") ||
                  (data?.status && data?.status != "incomplete")
                }
              >
                <AlertCircle className="h-4 w-4" />
                Additional Details
                {showCheckmark("priority") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              <TabsTrigger
                value="declaration"
                className="flex items-center gap-1"
                disabled={
                  isTabDisabled("declaration") ||
                  (data?.status && data?.status != "incomplete")
                }
              >
                <FileSignature className="h-4 w-4" />
                Declaration
                {showCheckmark("declaration") && (
                  <span className="ml-1 text-green-600">✓</span>
                )}
              </TabsTrigger>
              {data?.sanction?.status == "provisional_sanction_recommended" && (
                <>
                  <TabsTrigger
                    value="sanction_details"
                    className="flex items-center gap-1"
                    disabled={isTabDisabled("sanction_details")}
                  >
                    Sanction <br /> Details
                    {showCheckmark("sanction_details") && (
                      <span className="ml-1 text-green-600">✓</span>
                    )}
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="spv">
              <SpvDetails
                data={data}
                districts={districts}
                onTabComplete={() => handleTabComplete("spv")}
              />
            </TabsContent>
            <TabsContent value="eligibility">
              <EligibilityDetails
                data={data}
                onTabComplete={() => handleTabComplete("eligibility")}
              />
            </TabsContent>
            <TabsContent value="bank">
              <BankDetails
                data={data}
                onTabComplete={() => handleTabComplete("bank")}
              />
            </TabsContent>
            <TabsContent value="priority">
              <PriorityDetails
                data={data}
                file_name={"cfc_priority_factors_document"}
                setCurrentTab={setCurrentTab}
                onTabComplete={() => handleTabComplete("priority")}
              />
            </TabsContent>
            <TabsContent value="declaration">
              <DeclarationDetails
                data={data}
                onTabComplete={() => handleTabComplete("declaration")}
                declarationCompleted={declarationCompleted}
              />
            </TabsContent>
            <TabsContent value="sanction_details">
              <SanctionDetails
                data={data}
                onTabComplete={() => handleTabComplete("sanction_details")}
              />
            </TabsContent>
          </Tabs>
          <UploadRequirementsDialog
            openModal={openModal}
            setOpenModal={setOpenModal}
            uploads_array={uploads_array}
          />
        </>
      )}
    </div>
  );
};

export default CommonFacilityCentreForm;
